import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import ErrorMessage from "../../../form/ErrorMessage";
import Eye from "../../../form/Eye";
import { signInSchema } from "../../../../utils/schema";
import { authSignin } from "../../../../redux/features/coach/actions/authAction";
import { fetchfreelancerProfile } from "../../../../redux/features/find-a-job/actions/authAction";
import { setActivePublicPanel, setProfile, setToken } from "../../../../utils/pip";
import "./ApplyNowSignupModal.css";

const ApplyNowSignInModal = ({ isOpen = false, onClose, onSignIn, onBackToJobs }) => {
  const dispatch = useDispatch();
  const { isLoading } = useSelector((state) => state?.coach?.auth || {});
  const [showPassword, setShowPassword] = useState(false);
  const [apiError, setApiError] = useState("");

  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  const handleLogin = (values) => {
    setApiError("");
    dispatch(
      authSignin({
        payload: values,
        callback: (response, error) => {
          if (error) {
            setApiError(error?.data?.message || error?.message || "Login failed. Please try again.");
            return;
          }

          const isSuccess = response?.success === true || response?.status === true;
          if (!isSuccess) {
            setApiError(response?.message || "Login failed. Please try again.");
            return;
          }

          const payload = response?.data || response || {};
          const token =
            payload?.token ||
            payload?.access_token ||
            payload?.auth_token ||
            "";
          const userData = payload?.userData || payload?.user || {};
          const role = String(userData?.role || "").toUpperCase();

          if (role !== "OPEN_TALENT") {
            setApiError("Please login with your Open Talent account.");
            return;
          }

          setToken("freelancer", token);
          setActivePublicPanel("freelancer");
          setProfile("freelancer", userData);
          dispatch(fetchfreelancerProfile());

          const fullName =
            userData?.full_name ||
            `${userData?.first_name || ""} ${userData?.last_name || ""}`.trim();

          onSignIn?.({
            auth_token: token,
            email: userData?.email || values?.email || "",
            full_name: fullName,
            first_name: userData?.first_name || "",
            last_name: userData?.last_name || "",
            signup_step:
              userData?.signup_step ??
              payload?.signup_step ??
              payload?.data?.signup_step ??
              null,
          });
        },
      })
    );
  };

  const handleOverlayClick = (event) => {
    event.stopPropagation();
    if (event.target === event.currentTarget) {
      onClose?.();
    }
  };

  return createPortal(
    <div
      className="ct_signup_modal_overlay"
      role="dialog"
      aria-modal="true"
      onClick={handleOverlayClick}
    >
      <div
        className="ct_signin_modal_container bg-white" style={{ maxHeight: " min(98vh, 760px) " }}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="ct_signup_modal_close"
          onClick={onClose}
          aria-label="Close sign in modal"
        >
          <i className="fa-solid fa-xmark"></i>
        </button>

        <div className="ct_signin_modal_left ct_signup_modal_left_space_new">
          <img src="assets/img/unlock_more_img.png" alt="Sign in visual" />
        </div>

        <div className="ct_signin_modal_right ct_signup_modal_right_space_new">
          <div className="ct_signup_modal_logo_wrap">
            <img src="assets/img/Nuvio_logo.png" alt="Nuvio" />
          </div>

          <h3 className="ct_signin_modal_title">
            Stand Out with Your <span>NUVIO</span> Profile
          </h3>
          <p className="ct_signin_modal_subtitle">
            Complete your profile to get matched with the right opportunities faster
            and be discovered by top hirers.
          </p>

          <Formik
            initialValues={{
              email: "",
              password: "",
            }}
            validationSchema={signInSchema}
            onSubmit={(values) => {
              handleLogin(values);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
            }) => (
              <form className="ct_signup_modal_form ct_signin_modal_form" onSubmit={handleSubmit}>
                <div className="ct_signup_modal_group">
                  <label>Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={values.email}
                    onBlur={handleBlur}
                    onChange={(event) => {
                      setApiError("");
                      handleChange(event);
                    }}
                  />
                  <ErrorMessage errors={errors} touched={touched} fieldName="email" />
                </div>

                <div className="ct_signup_modal_group ct_signup_modal_password_group">
                  <label>Password</label>
                  <div className="position-relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      placeholder="************"
                      value={values.password}
                      onBlur={handleBlur}
                      onChange={(event) => {
                        setApiError("");
                        handleChange(event);
                      }}
                    />
                    <Eye isEye={showPassword} onClick={setShowPassword} />
                  </div>

                  <ErrorMessage errors={errors} touched={touched} fieldName="password" />
                </div>

                {apiError ? <p className="ct_modal_input_error mt-1">{apiError}</p> : null}

                <button
                  type="submit"
                  className="ct_signup_modal_continue ct_signin_modal_btn"
                  disabled={isLoading}
                >
                  {isLoading ? "Please wait..." : "Sign In"}
                </button>
              </form>
            )}
          </Formik>

          <button
            type="button"
            className="ct_signin_back_btn"
            onClick={() => onBackToJobs?.()}
          >
            <i className="fa-solid fa-arrow-left"></i> Back to Jobs
          </button>

          <div className="ct_signin_dots" aria-hidden="true">
            <span></span>
            <span className="active"></span>
            <span></span>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ApplyNowSignInModal;
